import { z } from 'zod';

// ─── Card shape ─────────────────────────────────────────────────────────────

export const SegmentCardSchema = z.object({
  headline: z.string(),
  summary: z.string(),
  tags: z.array(z.string()),
});

export type SegmentCard = z.infer<typeof SegmentCardSchema>;

// ─── Bucket definitions ─────────────────────────────────────────────────────

export const SEGMENT_CARD_BUCKETS = ['Context', 'Mindset', 'Behavior', 'Interaction', 'Drivers'] as const;

export type SegmentCardBucket = (typeof SEGMENT_CARD_BUCKETS)[number];

export interface SegmentCardDefinition {
  key: string;
  label: string;
  bucket: SegmentCardBucket;
}

export const SEGMENT_CARD_DEFINITIONS: SegmentCardDefinition[] = [
  // Context
  { key: 'cardPracticeModelRole', label: 'Practice Model & Role', bucket: 'Context' },
  { key: 'cardPatientMix', label: 'Patient Mix', bucket: 'Context' },
  { key: 'cardInstitutionalEnvironment', label: 'Institutional Environment', bucket: 'Context' },
  // Mindset
  { key: 'cardArchetype', label: 'Archetype', bucket: 'Mindset' },
  { key: 'cardRiskBenefitStance', label: 'Risk-Benefit Stance', bucket: 'Mindset' },
  { key: 'cardEvidenceOrientation', label: 'Evidence Orientation', bucket: 'Mindset' },
  // Behavior
  { key: 'card1lIntensityRegimen', label: '1L Intensity & Regimen Pattern', bucket: 'Behavior' },
  { key: 'cardAntiCd38Behavior', label: 'Anti-CD38 / Elderberry Behavior', bucket: 'Behavior' },
  { key: 'cardDurationSequencing', label: 'Duration & Sequencing', bucket: 'Behavior' },
  // Interaction
  { key: 'cardPatientDecisionStyle', label: 'Patient Decision Style', bucket: 'Interaction' },
  { key: 'cardInformationPreferences', label: 'Information Preferences', bucket: 'Interaction' },
  // Drivers
  { key: 'cardConstraints', label: 'Constraints', bucket: 'Drivers' },
  { key: 'cardMotivatorsChangeTriggers', label: 'Motivators & Change Triggers', bucket: 'Drivers' },
];

// ─── Allowed tags per card ──────────────────────────────────────────────────

export const CARD_ALLOWED_TAGS = {
  cardPracticeModelRole: [
    'Setting: Community',
    'Setting: Academic',
    'Setting: Mixed',
    'Org: Large network',
    'Org: Private practice',
    'Org: Integrated system',
    'Org: Hybrid',
    'Role mix: ONC-heavy',
    'Role mix: APP-heavy',
    'Role mix: Mixed',
    'Autonomy: High',
    'Autonomy: Medium-high',
    'Autonomy: Medium',
    'Autonomy: Low',
    'Environment: Pathway-driven',
    'Environment: Semi-flexible',
    'Environment: Flexible',
  ],
  cardPatientMix: [
    'Panel mix: TE-heavy',
    'Panel mix: Balanced',
    'Panel mix: TIE-heavy',
    'Frailty: High',
    'Frailty: Medium-high',
    'Frailty: Medium',
    'Frailty: Low',
    'Elderly: High',
    'Elderly: Medium-high',
    'Elderly: Medium',
    'Elderly: Low',
    'Cytogenetics salience: High',
    'Cytogenetics salience: Medium',
    'Cytogenetics salience: Low',
    'NDMM volume: High',
    'NDMM volume: Medium',
    'NDMM volume: Low',
  ],
  cardInstitutionalEnvironment: [
    'Pathway rigidity: High',
    'Pathway rigidity: Medium',
    'Pathway rigidity: Low',
    'Formulary constraint: High',
    'Formulary constraint: Medium',
    'Formulary constraint: Low',
    'Anti-CD38 environment: Elderberry-favored',
    'Anti-CD38 environment: Grape-favored',
    'Anti-CD38 environment: Neutral',
    'Anti-CD38 environment: Mixed',
    'Site of care: Outpatient-led',
    'Site of care: Mixed',
    'Site of care: Inpatient-sensitive',
    'Access friction: High',
    'Access friction: Medium',
    'Access friction: Low',
  ],
  cardArchetype: [
    'Primary archetype: Architect',
    'Primary archetype: Traditionalist',
    'Primary archetype: Strategist',
    'Primary archetype: Neophile',
    'Primary archetype: Warrior',
    'Primary archetype: Conservator',
    'Secondary archetype: Architect',
    'Secondary archetype: Traditionalist',
    'Secondary archetype: Strategist',
    'Secondary archetype: Neophile',
    'Secondary archetype: Warrior',
    'Secondary archetype: Conservator',
    'Secondary archetype: None',
    'Cohesion: High',
    'Cohesion: Medium',
    'Cohesion: Low',
  ],
  cardRiskBenefitStance: [
    'Treatment stance: Efficacy-first',
    'Treatment stance: Balanced',
    'Treatment stance: QoL-first',
    '1L worldview: Front-load best chance',
    '1L worldview: Stepwise journey',
    '1L worldview: Mixed',
    'De-intensification orientation: Rare',
    'De-intensification orientation: Selective',
    'De-intensification orientation: Common',
    'Disease-control style: Aggressive',
    'Disease-control style: Balanced',
    'Disease-control style: Conservative',
  ],
  cardEvidenceOrientation: [
    'Evidence anchor: NCCN-led',
    'Evidence anchor: OS-PFS-led',
    'Evidence anchor: MRD-led',
    'Evidence anchor: Peer-led',
    'Evidence anchor: Mixed',
    'MRD importance: High',
    'MRD importance: Medium',
    'MRD importance: Low',
    'RWE openness: High',
    'RWE openness: Medium',
    'RWE openness: Low',
    'Indirect comparison openness: High',
    'Indirect comparison openness: Medium',
    'Indirect comparison openness: Low',
    'Innovation posture: Early adopter',
    'Innovation posture: Selective adopter',
    'Innovation posture: Proven-only',
  ],
  card1lIntensityRegimen: [
    'Default 1L intensity: Doublet-first',
    'Default 1L intensity: Triplet-first',
    'Default 1L intensity: Quad-forward',
    'Default 1L intensity: Mixed',
    'TE pattern: Doublet',
    'TE pattern: Triplet',
    'TE pattern: Quad',
    'TE pattern: Mixed',
    'TENI pattern: Doublet',
    'TENI pattern: Triplet',
    'TENI pattern: Quad',
    'TENI pattern: Mixed',
    'TIE pattern: Doublet',
    'TIE pattern: Triplet',
    'TIE pattern: Quad',
    'TIE pattern: Mixed',
    'Intensity flexibility: High',
    'Intensity flexibility: Medium',
    'Intensity flexibility: Low',
  ],
  cardAntiCd38Behavior: [
    'Anti-CD38 role: Broad 1L',
    'Anti-CD38 role: Selective 1L',
    'Anti-CD38 role: Reserve for later',
    'Anti-CD38 role: Rare use',
    'Elderberry position: Default backbone',
    'Elderberry position: Situational',
    'Elderberry position: Later-line preferred',
    'Elderberry position: Avoided',
    'Grape position: Preferred alternative',
    'Grape position: Niche alternative',
    'Grape position: Rare use',
    'Grape position: None',
    'Save for later tendency: High',
    'Save for later tendency: Medium',
    'Save for later tendency: Low',
    'Elderberry breadth: Broad',
    'Elderberry breadth: Moderate',
    'Elderberry breadth: Narrow',
  ],
  cardDurationSequencing: [
    'Duration style: Treat-to-progression',
    'Duration style: Response-based de-escalation',
    'Duration style: Fixed-duration leaning',
    'Duration style: Mixed',
    'De-escalation frequency: High',
    'De-escalation frequency: Medium',
    'De-escalation frequency: Low',
    'First component dropped: PI',
    'First component dropped: IMiD',
    'First component dropped: Anti-CD38',
    'First component dropped: Steroid',
    'First component dropped: Mixed',
    'Sequencing logic: Front-load best',
    'Sequencing logic: Preserve later options',
    'Sequencing logic: Patient-fit first',
    'Sequencing logic: Mixed',
    'Attrition concern: High',
    'Attrition concern: Medium',
    'Attrition concern: Low',
  ],
  cardPatientDecisionStyle: [
    'Decision style: Physician-led',
    'Decision style: Structured SDM',
    'Decision style: Preference-accommodating',
    'Decision style: Mixed',
    'Recommendation strength: Strong',
    'Recommendation strength: Moderate',
    'Recommendation strength: Light',
    'Flexibility: High',
    'Flexibility: Medium',
    'Flexibility: Low',
    'Holiday openness: High',
    'Holiday openness: Medium',
    'Holiday openness: Low',
    'Burden sensitivity: High',
    'Burden sensitivity: Medium',
    'Burden sensitivity: Low',
  ],
  cardInformationPreferences: [
    'Primary source: NCCN-journals',
    'Primary source: Congress-peers',
    'Primary source: Rep-MSL',
    'Primary source: Digital-email',
    'Primary source: Mixed',
    'Preferred channel: Journal',
    'Preferred channel: Congress',
    'Preferred channel: Rep',
    'Preferred channel: MSL',
    'Preferred channel: Email',
    'Preferred channel: Website',
    'Preferred channel: Peer discussion',
    'Preferred format: Headline + proof',
    'Preferred format: Full data',
    'Preferred format: Case-based discussion',
    'Preferred format: Mixed',
    'Rep/MSL openness: High',
    'Rep/MSL openness: Medium',
    'Rep/MSL openness: Low',
    'Digital engagement: High',
    'Digital engagement: Medium',
    'Digital engagement: Low',
  ],
  cardConstraints: [
    'Primary constraint: Pathway',
    'Primary constraint: Formulary',
    'Primary constraint: Toxicity',
    'Primary constraint: Logistics',
    'Primary constraint: Cost',
    'Primary constraint: Staffing',
    'Primary constraint: Mixed',
    'Constraint intensity: High',
    'Constraint intensity: Medium',
    'Constraint intensity: Low',
    'Friction type: Clinical',
    'Friction type: Operational',
    'Friction type: Access',
    'Friction type: Patient-driven',
    'Friction type: Mixed',
    'Modifiability: Yes',
    'Modifiability: Partial',
    'Modifiability: No',
    'External barrier: Access',
    'External barrier: Caregiver burden',
    'External barrier: Travel',
    'External barrier: Reimbursement',
    'External barrier: None',
  ],
  cardMotivatorsChangeTriggers: [
    'Primary motivator: Deep response',
    'Primary motivator: Durability',
    'Primary motivator: QoL preservation',
    'Primary motivator: Simplicity',
    'Primary motivator: Access',
    'Primary motivator: Mixed',
    'Clinical goal: Response-maximizing',
    'Clinical goal: Balance-maximizing',
    'Clinical goal: Burden-minimizing',
    'Primary trigger: Strong OS data',
    'Primary trigger: NCCN update',
    'Primary trigger: Easier administration',
    'Primary trigger: Formulary change',
    'Primary trigger: Peer uptake',
    'Primary trigger: Mixed',
    'Tone: Confident',
    'Tone: Pragmatic',
    'Tone: Cautious',
    'Tone: Aggressive',
    'Tone: Empathetic',
    'Tone: Mixed',
    'Change readiness: High',
    'Change readiness: Medium',
    'Change readiness: Low',
  ],
} as const;

export type CardKey = keyof typeof CARD_ALLOWED_TAGS;

// ─── Bucket color scheme ────────────────────────────────────────────────────

export const BUCKET_COLORS: Record<SegmentCardBucket, { text: string; bg: string }> = {
  Context: { text: '#8B6914', bg: '#FAF0DC' },
  Mindset: { text: '#3D5A80', bg: '#E8EEF4' },
  Behavior: { text: '#2D6A4F', bg: '#E4F0EA' },
  Interaction: { text: '#5B3A8C', bg: '#EDEBF5' },
  Drivers: { text: '#8C6D1F', bg: '#FDF6E3' },
};
